// useUserBadges — fetches all badges owned by a user. Used by:
//   - Profile hero (filter to is_featured = true + expires_at IS NULL)
//   - /you/badges curation page (full list, owner-only)
//
// Auto-refetches on focus + on a 60s staleTime so cron-driven changes
// (Brand Authority handoffs every Monday) propagate without a hard refresh.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type BadgeKind =
  // Personal milestones (all permanent in the new model)
  | "collector_tier"        // single row per user, metadata.level promotes
  | "era_milestone"         // per-decade milestone (5/10/20 thresholds)
  | "brand_authority"       // REPURPOSED: 20+ PASS scans of one brand
  | "diverse_collector"     // time_traveler variant (4+ eras)
  | "region_collector"      // 6 variants (hawaii_5o, harajuku_hunter, ...)
  | "style_collector"       // 3 variants (gossip_girl, sports_connoisseur, ...)
  | "deadstock_collector"   // 3+ deadstock PASS items
  | "community_streak"      // bestie / cheerleader / photo_pro / stack_master / grail_granter
  // Community / cohort
  | "fotw_winner"
  | "fotw_candidate"
  | "founder"
  | "verified_moderator"
  // Legacy — no new awards. Existing rows render with subdued styling.
  | "era_specialist";

export interface UserBadge {
  id: string;
  user_id: string;
  kind: BadgeKind;
  metadata: Record<string, string | number | boolean | null>;
  awarded_at: string;
  expires_at: string | null;
  is_featured: boolean;
}

interface UseUserBadgesOptions {
  /** Include expired rolling badges (held in the past, not current). Default false. */
  includeExpired?: boolean;
}

// All redesigned badges are permanent. Only fotw_candidate stays rolling
// (weekly reissue). Legacy era_specialist rows still display but are
// flagged via isLegacy().
const PERMANENT_KINDS: BadgeKind[] = [
  "collector_tier",
  "era_milestone",
  "brand_authority",
  "diverse_collector",
  "region_collector",
  "style_collector",
  "deadstock_collector",
  "community_streak",
  "fotw_winner",
  "founder",
  "verified_moderator",
];
const ROLLING_KINDS: BadgeKind[] = ["fotw_candidate"];
const LEGACY_KINDS: BadgeKind[] = ["era_specialist"];

export function isPermanent(kind: BadgeKind): boolean {
  return PERMANENT_KINDS.includes(kind);
}

export function isRolling(kind: BadgeKind): boolean {
  return ROLLING_KINDS.includes(kind);
}

export function isLegacy(kind: BadgeKind): boolean {
  return LEGACY_KINDS.includes(kind);
}

export function useUserBadges(
  userId: string | null | undefined,
  options: UseUserBadgesOptions = {},
) {
  const queryClient = useQueryClient();
  const { includeExpired = false } = options;

  const query = useQuery({
    queryKey: ["user-badges", userId, { includeExpired }],
    enabled: !!userId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<UserBadge[]> => {
      if (!userId) return [];
      let q = supabase
        .from("user_badges")
        .select("*")
        .eq("user_id", userId)
        .order("awarded_at", { ascending: false });
      if (!includeExpired) {
        q = q.is("expires_at", null);
      }
      const { data, error } = await q;
      if (error) {
        console.warn("[useUserBadges] fetch failed", error);
        return [];
      }
      return (data ?? []) as UserBadge[];
    },
  });

  /** Force a refetch — call after set_featured_badges or new PASS scan. */
  const invalidate = () => {
    if (!userId) return;
    void queryClient.invalidateQueries({ queryKey: ["user-badges", userId] });
  };

  const all = query.data ?? [];
  const featured = all.filter((b) => b.is_featured);

  return {
    badges: all,
    featured,
    isLoading: query.isLoading,
    invalidate,
  };
}
